import { domToReact, type HTMLReactParserOptions } from 'html-react-parser';
import type { ReactNode } from 'react';
import type { JSX } from 'react/jsx-runtime';
import { CdRatesWidget, SavingsWidget } from '@/app_components/common/widgets/RatesWidget';
import { RedVentureWidgets } from '@/app_data/widgets/RedVentures/constants';
import type { DOMNode, DomElement } from '../types';
import { isElement } from '../types';
import { LinkParser } from './anchorParser';
import { DetailParser, SummaryParser } from './detailSummaryParser';
import { IframeParser } from './iframeParser';
import { ImageParser } from './imageParser';
import { ScriptParser } from './scriptParser';
import { WidgetParser } from './widgetParser';

function parseBaseComponents(domNode: DomElement, reactNode: ReactNode, index: number): null | JSX.Element | ReactNode {
  const { name, attribs } = domNode;

  try {
    if (attribs['data-widget-id']) {
      return (
        <WidgetParser
          domNode={domNode}
          key={index}
          reactNode={reactNode}
        />
      );
    }

    /* Red Venture Widgets */
    if (attribs['data-ad-id'] === RedVentureWidgets.SavingsMma) {
      return <SavingsWidget key={attribs['data-ad-id']} />;
    }
    if (attribs['data-ad-id'] === RedVentureWidgets.CdRates) {
      return <CdRatesWidget key={attribs['data-ad-id']} />;
    }

    /* ClassName Replacers here */

    /* ClassName Replacers here */

    /* HTML Tag Replacers */
    switch (name) {
      // TODO: seems to be an issue with some iframes/scripts on personal finance evergreen articles
      case 'iframe':
        return (
          <IframeParser
            domNode={domNode}
            key={index}
            reactNode={reactNode}
          />
        );
      case 'img':
        return (
          <ImageParser
            domNode={domNode}
            key={index}
            reactNode={reactNode}
          />
        );
      case 'a':
        return (
          <LinkParser
            domNode={domNode}
            key={index}
            reactNode={reactNode}
          />
        );
      case 'details':
        return (
          <DetailParser
            domNode={domNode}
            key={index}
            reactNode={reactNode}
          />
        );
      case 'summary':
        return (
          <SummaryParser
            domNode={domNode}
            key={index}
            reactNode={reactNode}
          />
        );
      case 'script':
        return (
          <ScriptParser
            domNode={domNode}
            key={index}
            reactNode={reactNode}
          />
        );
      default:
        return null;
    }
  } catch (error) {
    // biome-ignore lint/suspicious/noConsole: need error
    console.error(`Error parsing node: ${error}`);
    return reactNode;
  }
}

function transformNode(domNode: DOMNode, reactNode: ReactNode, index: number): ReactNode {
  if (!isElement(domNode)) {
    return reactNode;
  }

  const baseParsedResults = parseBaseComponents(domNode, reactNode, index);
  if (baseParsedResults) {
    return baseParsedResults;
  }

  return reactNode;
}

export const options: HTMLReactParserOptions = {
  transform(reactNode: ReactNode, domNode: DOMNode, index: number) {
    //  raw-html-parser types are not up to date
    return transformNode(domNode, reactNode, index) as JSX.Element;
  },
  trim: true,
};

// biome-ignore lint/suspicious/noExplicitAny: ts issues
export function renderChild(childNode: (any | DOMNode)[]): ReactNode {
  return domToReact(childNode, options);
}
